// src/components/(blog)/post/PostCommentForm.tsx

'use client';

import React, { ChangeEvent, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Reviews, {
    CommentNode,
    CommentsData,
} from '@/components/(postTypes)/platforms/(archive)/layout/(review)/list/Reviews';
import { SmsEdit } from 'iconsax-react';
import { validateContent } from '@/utils/post/comments/ReviewValidation';
import { createComment } from '@/utils/post/comments/createComment';
import { useToast } from '@/hooks/use-toast';
import { usePrefillUserData } from '@/hooks/usePrefillUserData';
import useVerifyUser from '@/hooks/profile/useVerifyUser';
import LoginFormComponent from '@/components/(dashboard)/auth/LoginFormComponent';
import NameInput from '@/components/(dashboard)/login-register/NameInput';
import { updateUser } from '@/utils/profile/updateUser';
import { mutate } from 'swr';
import { toPersianDigits } from '@/utils/persianUtils';

const containsForbiddenContent = (text: string) => {
    const htmlPattern = /<\/?[a-z][\s\S]*>/i;
    const urlPattern = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
    return htmlPattern.test(text) || urlPattern.test(text);
};

const stripForbiddenContent = (text: string) => {
    let cleanText = text.replace(/<\/?[^>]+(>|$)/g, '');
    cleanText = cleanText.replace(/(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi, '');
    return cleanText;
};

interface FormErrors {
    content: string;
    [key: string]: string;
}

interface FormData {
    content: string;
    errors: FormErrors;
}

interface PostCommentFormProps {
    postId: number;
    commentCount: number;
    customPlaceholder?: string;
    comments?: CommentNode[];
    hasNextPage?: CommentsData['comments']['pageInfo']['hasNextPage'];
    onSuccess?: () => void;
    prefilledContent?: string;
    showForm?: boolean;
}

const initialFormState: FormData = {
    content: '',
    errors: { content: '' },
};

const PostCommentForm: React.FC<PostCommentFormProps> = ({
    postId,
    commentCount,
    customPlaceholder,
    comments,
    hasNextPage,
    onSuccess,
    prefilledContent,
    showForm = true,
}) => {
    const { prefilledName, prefilledEmail } = usePrefillUserData();
    const { isLoggedInGlobal } = useVerifyUser();
    const [formData, setFormData] = useState<FormData>(initialFormState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modalStep, setModalStep] = useState<'login' | 'name'>('login');
    const [nameInputVal, setNameInputVal] = useState<string>('');
    const [emailInputVal, setEmailInputVal] = useState<string>('');
    const { toast } = useToast();

    useEffect(() => {
        if (prefilledName) setNameInputVal(prefilledName);
        if (prefilledEmail) setEmailInputVal(prefilledEmail);
    }, [prefilledName, prefilledEmail]);

    useEffect(() => {
        if (prefilledContent) {
            setFormData((prev) => ({ ...prev, content: prefilledContent }));
        }
    }, [prefilledContent]);

    const openModal = () => {
        let hasError = false;
        const newErrors: FormErrors = { ...formData.errors };

        // 1. Validate content
        if (formData.content.trim() === '') {
            newErrors.content = validateContent(formData.content);
            hasError = true;
        }

        if (hasError) {
            setFormData({ ...formData, errors: newErrors });
            return;
        }

        if (!isLoggedInGlobal) {
            setModalStep('login');
            setIsModalOpen(true);
        } else if (!prefilledName) {
            setModalStep('name');
            setIsModalOpen(true);
        } else {
            submitCommentDirectly(prefilledName);
        }
    };

    const closeModal = () => setIsModalOpen(false);

    const submitCommentDirectly = async (authorName: string, authorEmail?: string) => {
        setIsSubmitting(true);
        try {
            const resCommentId: number = await createComment(
                {
                    author: authorName,
                    authorEmail: authorEmail || emailInputVal,
                    content: formData.content,
                },
                postId,
            );

            if (resCommentId > 0) {
                toast({
                    className: 'bg-green-800 text-white rounded-2xl',
                    title: 'نظر شما با موفقیت ثبت شد',
                    description: 'پس از تایید مدیر نمایش داده می‌شود',
                });

                onSuccess?.();
                closeModal();
                setFormData(initialFormState);
            } else {
                // FIX: Handle the generic failure directly instead of throwing an error
                toast({
                    className: 'bg-red-800 text-white rounded-2xl',
                    title: 'خطا در ثبت دیدگاه',
                    description: 'مشکلی پیش‌آمد.',
                });
            }
        } catch (err: any) {
            // This catch block now only catches ACTUAL network or system errors
            console.error(err);
            let errorMessage = err.message || 'در فرایند ثبت نظر شما مشکلی پیش آمد!';

            if (errorMessage.includes('Duplicate comment')) {
                errorMessage = 'شما قبلا این دیدگاه را ثبت کرده‌اید (دیدگاه تکراری).';
            } else if (errorMessage.includes('too quickly')) {
                errorMessage = 'شما با سرعت زیادی در حال ثبت دیدگاه هستید. کمی صبر کنید.';
            }

            toast({
                className: 'bg-red-800 text-white rounded-2xl',
                title: 'خطا در ثبت دیدگاه',
                description: errorMessage,
            });
        } finally {
            setIsSubmitting(false);
        }
    };
    const handleLoginSuccess = async () => {
        const updatedUserRes = await mutate('verifyUser');
        const updatedUser = updatedUserRes?.data?.user;
        const firstName = (updatedUser?.firstName ?? '').trim();
        const email = (updatedUser?.email ?? '').trim();
        const blockName = firstName.startsWith('کاربر با موبایل') || !firstName;
        const blockEmail = email.startsWith('erp_client') || !email;

        if (blockName || blockEmail) {
            setModalStep('name');
        } else {
            closeModal();
            submitCommentDirectly(firstName || prefilledName, email || prefilledEmail);
        }
    };

    const handleNameSubmit = async (fullName: string) => {
        setIsSubmitting(true);
        try {
            if (isLoggedInGlobal) {
                const res = await updateUser({ fullName, email: emailInputVal });
                if (res && res.success) {
                    await mutate('verifyUser');
                }
            }

            closeModal();
            await submitCommentDirectly(fullName, emailInputVal);
        } catch (err) {
            console.error('Error updating name:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'content') {
            const hasForbidden = containsForbiddenContent(value);
            const cleanValue = stripForbiddenContent(value);
            setFormData({
                ...formData,
                [name]: cleanValue,
                errors: {
                    ...formData.errors,
                    [name]: hasForbidden ? 'استفاده از لینک و کدهای HTML مجاز نیست.' : '',
                },
            });
        }
    };

    return (
        <>
            {showForm && (
                <div className={'mb-5 flex flex-col lg:items-end w-full'}>
                    {formData?.errors?.content && (
                        <p className='my-2 text-center lg:text-right text-sm font-bold text-red-500'>
                            {formData?.errors?.content}
                        </p>
                    )}
                    <div className={'relative group w-full'}>
                        <textarea
                            className={`mb-4 block min-h-24 w-full resize-y rounded-2xl border bg-gray-100 dark:bg-white/5 border-gray-200 dark:border-white/10 py-4 pe-4 ps-10 text-base leading-7 placeholder:text-sm placeholder:text-gray-500 dark:placeholder:text-white/40 outline-none transition-colors focus:border-progress focus:ring-2 focus:ring-progress/20 disabled:opacity-60 ${formData?.errors?.content ? 'border-red-400 dark:border-red-400/60' : ''}`}
                            id='content'
                            name='content'
                            aria-label='متن نظر'
                            aria-invalid={!!formData?.errors?.content}
                            value={formData?.content}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                            rows={3}
                            placeholder={customPlaceholder ?? 'نظر شما...'}
                        ></textarea>
                        <SmsEdit
                            aria-hidden
                            className={
                                'pointer-events-none absolute start-3.5 top-[1.15rem] z-10 opacity-80 dark:stroke-white/60 stroke-primary/80'
                            }
                            size={18}
                        />
                    </div>

                    <button
                        type='button'
                        onClick={openModal}
                        disabled={isSubmitting}
                        className='w-full rounded-xl border border-progress bg-progress hover:brightness-125 px-8 py-2 text-white transition-all duration-300 xl:w-fit disabled:opacity-50'
                    >
                        {isSubmitting ? 'در حال ارسال...' : 'ثبت نظر'}
                    </button>
                </div>
            )}

            <Dialog open={isModalOpen} onOpenChange={closeModal}>
                <DialogContent className='max-w-[95%] mx-auto overflow-hidden dark:border-white/20 rounded-2xl bg-white p-6 dark:bg-primary-dark lg:max-w-md'>
                    {modalStep === 'login' ? (
                        <>
                            <DialogHeader dir='rtl'>
                                <DialogTitle className='mb-5 text-center text-lg leading-6 text-gray-900 dark:text-white'>
                                    ورود یا ثبت نام
                                </DialogTitle>
                            </DialogHeader>
                            <LoginFormComponent handleLoginSuccess={handleLoginSuccess} />
                        </>
                    ) : (
                        <>
                            <DialogHeader dir='rtl'>
                                <DialogTitle className='mb-5 text-center text-lg leading-6 text-gray-900 dark:text-white'>
                                    لطفا نام و نام‌خانوادگی خود را وارد کنید
                                </DialogTitle>
                            </DialogHeader>
                            <div className='w-full text-right' dir='rtl'>
                                <NameInput
                                    value={nameInputVal}
                                    onChange={setNameInputVal}
                                    email={emailInputVal}
                                    setEmail={setEmailInputVal}
                                    onSubmit={handleNameSubmit}
                                    loading={isSubmitting}
                                    textBtn='ثبت و ارسال دیدگاه'
                                />
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {commentCount > 0 && (
                <div className={'my-5 w-full'}>
                    <div
                        className={
                            'relative flex items-center gap-x-1 px-3 border-t pt-7 border-gray-200 dark:border-white/20'
                        }
                    >
                        <p className={'font-base'}>همه نظرات</p>
                        <span className='font-thin text-sm'>
                            ({toPersianDigits(commentCount)} نظر)
                        </span>
                    </div>
                    <Reviews
                        currentPostId={postId}
                        commentCount={commentCount}
                        comments={comments}
                        hasNextPage={hasNextPage}
                    />
                </div>
            )}
        </>
    );
};

export default PostCommentForm;
